'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { ShieldCheck, Truck, Search, Lock, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface FeaturedCard {
  id: string;
  title: string;
  images: string[];
}

export default function LandingPage() {
  const supabase = createClient();
  const [featuredCards, setFeaturedCards] = useState<FeaturedCard[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await supabase
          .from('listings')
          .select('id, title, images')
          .eq('is_active', true)
          .eq('featured', true)
          .order('created_at', { ascending: false })
          .limit(12);

        if (data && data.length > 0) {
          setFeaturedCards(data);
        } else {
          // Fallback: fetch any active listings
          const { data: anyData } = await supabase
            .from('listings')
            .select('id, title, images')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(12);
          setFeaturedCards(anyData || []);
        }
      } catch {
        setFeaturedCards([]);
      } finally {
        setLoadingCards(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* ── Top Navigation Bar ── */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800 flex items-center px-4 md:px-6 gap-3">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 shrink-0">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-white hidden sm:block tracking-tight">
            CardTrader<span className="text-violet-400">TT</span>
          </span>
        </Link>

        {/* Search bar — desktop (decorative, prompts sign-in) */}
        <div className="hidden md:flex flex-1 max-w-xl mx-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search cards, sets, sellers..."
            readOnly
            onClick={() => window.location.href = '/sign-up-login-screen'}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-4 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-violet-500 cursor-pointer"
          />
        </div>

        <div className="flex-1 md:hidden" />

        {/* Sign In / Sign Up buttons */}
        <div className="flex items-center gap-2">
          <Link
            href="/sign-up-login-screen"
            className="text-sm text-zinc-300 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-zinc-800"
          >
            Sign In
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white px-4 py-1.5 rounded-lg transition-colors"
          >
            Sign Up
          </Link>
        </div>
      </header>
      {/* ── Main Content ── */}
      <main className="flex-1 pt-16 pb-20 md:pb-0">
        {/* Hero / Banner Section */}
        <div className="relative w-full overflow-hidden">
          <img
            src="/assets/images/Pokemon-Destined-Rivals-Homepage-Banner-png-1774201224105.png"
            alt="Pokémon Destined Rivals - new set available now on CardTraderTT"
            className="w-full object-cover h-56 sm:h-72 md:h-80 lg:h-96"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-zinc-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900/30 via-transparent to-amber-900/20" />

          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-5 pt-8">
            <div className="max-w-screen-2xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-1.5 bg-violet-600/80 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    New Set Available
                  </span>
                  <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                    CardTrader <span className="text-violet-400">TT</span>
                  </h1>
                  <p className="text-zinc-300 text-sm mt-1 drop-shadow">
                    Trading Card Marketplace · Trinidad &amp; Tobago&apos;s #1 platform · TT$ pricing · PayWise &amp; PayPal accepted
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-300 shrink-0">
                  <div className="flex items-center gap-1.5 bg-zinc-900/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-zinc-700/60">
                    <ShieldCheck size={13} className="text-emerald-400" />
                    <span>Secure Checkout</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-zinc-900/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-zinc-700/60">
                    <Truck size={13} className="text-blue-400" />
                    <span>Local Delivery</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-zinc-900/70 backdrop-blur-sm rounded-lg px-3 py-2 border border-zinc-700/60">
                    <Lock size={13} className="text-amber-400" />
                    <span>PayWise &amp; PayPal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thin accent border */}
        <div className="h-px bg-gradient-to-r from-violet-600/40 via-amber-500/20 to-transparent" />

        {/* Featured Cards Block */}
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 xl:px-10 2xl:px-16 py-8">
          {/* Section header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">Featured Listings</h2>
              <p className="text-sm text-zinc-400 mt-0.5">Sign in to view pricing, details &amp; purchase cards</p>
            </div>
            <Link
              href="/sign-up-login-screen"
              className="flex items-center gap-2 text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Lock size={14} />
              Sign In to Browse
            </Link>
          </div>

          {/* Cards grid */}
          {loadingCards ? (
            <div className="flex items-center justify-center py-16 gap-2">
              <Loader2 size={20} className="text-violet-400 animate-spin" />
              <span className="text-zinc-400 text-sm">Loading listings...</span>
            </div>
          ) : featuredCards.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {featuredCards.map((card) => (
                <Link
                  key={card.id}
                  href="/sign-up-login-screen"
                  className="group relative bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-violet-500/50 transition-all duration-200 hover:shadow-lg hover:shadow-violet-500/10 cursor-pointer"
                >
                  <div className="aspect-[3/4] w-full overflow-hidden bg-zinc-800">
                    {card.images?.[0] ? (
                      <img
                        src={card.images[0]}
                        alt={`Featured trading card: ${card.title}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                        <span className="text-zinc-600 text-xs text-center px-2">{card.title}</span>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-zinc-950/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <div className="w-9 h-9 bg-violet-600 rounded-full flex items-center justify-center">
                        <Lock size={16} className="text-white" />
                      </div>
                      <span className="text-xs font-semibold text-white text-center px-2">Sign in to view</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3 bg-zinc-900 border border-zinc-800 rounded-2xl">
              <div className="w-14 h-14 rounded-2xl bg-zinc-800 flex items-center justify-center">
                <Lock size={24} className="text-zinc-600" />
              </div>
              <p className="font-semibold text-zinc-300">Listings are live!</p>
              <p className="text-sm text-zinc-500 text-center max-w-xs">
                Sign in to browse all available trading cards from sellers across Trinidad &amp; Tobago.
              </p>
              <Link
                href="/sign-up-login-screen"
                className="mt-2 text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white px-6 py-2.5 rounded-lg transition-colors"
              >
                Sign In to Browse
              </Link>
            </div>
          )}

          {/* CTA below grid */}
          {featuredCards.length > 0 && (
            <div className="mt-8 text-center">
              <div className="inline-flex flex-col items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-2xl px-8 py-6">
                <p className="text-zinc-300 text-sm font-medium">Join CardTraderTT to see full listings, pricing &amp; more</p>
                <div className="flex items-center gap-3">
                  <Link
                    href="/sign-up-login-screen"
                    className="text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white px-6 py-2.5 rounded-lg transition-colors"
                  >
                    Create Free Account
                  </Link>
                  <Link
                    href="/sign-up-login-screen"
                    className="text-sm text-zinc-400 hover:text-zinc-200 transition-colors"
                  >
                    Already have an account? Sign in
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      {/* ── Landing Bottom Nav — Sign In only ── */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800 h-16 flex items-center justify-center px-6">
        <Link
          href="/sign-up-login-screen"
          className="w-full max-w-xs flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm py-3 rounded-xl transition-colors"
        >
          Sign In to CardTraderTT
        </Link>
      </nav>
    </div>
  );
}
